import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { GenderIdentity } from "@/Common/Models/Individuals/Enums/GenderIdentity";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { EditMode } from "@/Common/Models/Patient/PatientEnums";
import { IPatientDemographicsModel } from "@/Common/Models/Patient/UI/Profile/IPatientDemographicsModel";
import { ReligionCache } from "@/Common/Models/Patient/UI/Profile/ReligionModel";
import { EthnicityDropdownItem } from "@/Common/Models/UI/Inputs/EthnicityDropdownItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class PatientProfileDemographicsHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_EditClick, this.DemographicsEditClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_CancelClick, this.DemographicsCancelClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_SaveClick, this.DemographicsSaveClick);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_SelectPrefix, this.DemographicsSelectPrefix);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_FirstNameChange, this.DemographicsFirstNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_LastNameChange, this.DemographicsLastNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_PreferredNameChange, this.DemographicsPreferredNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_SelectSuffix, this.DemographicsSuffixSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_SelectPreferredPronouns, this.DemographicsPreferredPronounsSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_SpecifiedPreferredPronounsChange, this.DemographicsSpecifiedPreferredPronounsChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_MiddleNameChange, this.DemographicsMiddleNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_MothersFirstNameChange, this.DemographicsMothersFirstNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_MothersLastNameChange, this.DemographicsMothersLastNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_FathersFirstNameChange, this.DemographicsFathersFirstNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_FathersLastNameChange, this.DemographicsFathersLastNameChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_SocialInsuranceChange, this.DemographicsSocialInsuranceChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_GenderSelect, this.DemographicsGenderSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_GenderIdentityChange, this.DemographicsGenderIdentityChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_ReligionChange, this.DemographicsReligionChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_EthnicitySelect, this.DemographicsEthnicitySelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_EthnicityClear, this.DemographicsEthnicityClear);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_DateOfBirthChange, this.DemographicsDateOfBirthChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_LegalStatusSelect, this.DemographicsLegalStatusSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_OfficialLanguageSelect, this.DemographicsOfficialLanguageSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_SpokenLanguageSelect, this.DemographicsSpokenLanguageSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_PatientStatusSelect, this.DemographicsPatientStatusSelect);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_PatientStatusDateChange, this.DemographicsPatientStatusDateChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_ProfessionChange, this.DemographicsProfessionChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_EmployerChange, this.DemographicsEmployerChange);
        this.RegisterOperation(LocalPatientOperations.Profile.Demographics_NotesChange, this.DemographicsNotesChange);
    }

    public DemographicsEditClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let modelToUndo: IPatientDemographicsModel = store.PatientProfileDemographicModels[request.PatientId];

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.Edit);
        modelToUndo = modelToUndo.set("PatientContactId", modelToUndo.get("PatientContactId")); // to get copy/clone of immutable model

        store.PatientProfileDemographicUndoModels[request.PatientId] = modelToUndo;
        store.PatientUIModels[request.PatientId] = uiModel;
    }

    public DemographicsCancelClick(request: LocalEvent, store: PatientStore): void {
        let uiModel = store.PatientUIModels[request.PatientId];
        let undoModel: IPatientDemographicsModel = store.PatientProfileDemographicUndoModels[request.PatientId];

        uiModel = uiModel.set("PatientProfileEditMode", EditMode.View);

        store.PatientProfileDemographicModels[request.PatientId] = undoModel;
        store.PatientUIModels[request.PatientId] = uiModel;
        delete store.PatientProfileDemographicUndoModels[request.PatientId];
    }

    private DemographicsSaveClick(request: LocalEvent, store: PatientStore): void {
        const { PatientId } = request;
        const uiModel = store.PatientUIModels[PatientId].set("LockPatientDemographicSaveButton", true);
        const demographicsModel = store.PatientProfileDemographicModels[PatientId];
        store.PatientUIModels[request.PatientId] = uiModel;
        store.ActionsProfile.savePatientDemographics(demographicsModel);
    }

    private DemographicsSelectPrefix(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("SelectedPrefixLookupId", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsFirstNameChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("FirstName", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsLastNameChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("LastName", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsPreferredNameChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("PreferredName", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsMiddleNameChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("MiddleName", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsSuffixSelect(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("SelectedSuffixLookupId", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsPreferredPronounsSelect(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        const value = request.Get<PreferredPronouns>("value");
        if (value != PreferredPronouns.Other) {
            demoModel = demoModel.set("SpecifiedPreferredPronouns", null);
        }
        store.PatientProfileDemographicModels[request.PatientId] = demoModel.set("PreferredPronouns", value);
    }

    private DemographicsSpecifiedPreferredPronounsChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        const value = request.Get<string>("value");
        store.PatientProfileDemographicModels[request.PatientId] = demoModel.set("SpecifiedPreferredPronouns", value);
    }

    private DemographicsMothersFirstNameChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("MothersFirstName", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsMothersLastNameChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("MothersLastName", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsSocialInsuranceChange(request: LocalEvent, store: PatientStore): void {
        let value = request.Get<string>("value");
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let patient = store.PatientModels[request.PatientId];

        let isSSNValid = true;

        if (!(patient == null)) {
            // fetch ssn validation info
            let validationHelper = new ValidationHelper();
            let validationRule = validationHelper.getSSNValidationRule(patient.GetCountryLookupId());
            let validationRegex: string = validationRule ? validationRule.ValidationRegexPattern : "";

            if (!(validationRegex == null)) {
                let regexp = new RegExp(validationRegex, "i");
                isSSNValid = ValidationHelper.isExactMatch(value, regexp);
                demoModel = demoModel.set("IsSSNValid", isSSNValid);
            }
        }

        demoModel = demoModel.set("SocialInsuranceNumber", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsGenderSelect(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("SelectedGenderLookupId", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsEthnicitySelect(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<EthnicityDropdownItem>("value");
        let ids = demoModel.get("SelectedEthnicityKeys").toArray();
        ids.push(value.Item.value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel.set("SelectedEthnicityKeys", ids.toImmutable());
    }

    private DemographicsEthnicityClear(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<EthnicityDropdownItem>("value");
        let ids = demoModel.get("SelectedEthnicityKeys").toArray();

        const index = ids.indexOf(value.Item.value);

        ids.splice(index, 1);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel.set("SelectedEthnicityKeys", ids.toImmutable());
    }

    private DemographicsGenderIdentityChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<GenderIdentity>("value");
        demoModel = demoModel.set("GenderIdentity", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsReligionChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("code");

        demoModel = demoModel.set("Religion", new ReligionCache(cacheStore.GetReligions().filter(b => b.Code === value)[0]));
        demoModel = demoModel.set("ReligionCode", value);

        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsDateOfBirthChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<Date>("value");
        demoModel = demoModel.set("DateOfBirth", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsLegalStatusSelect(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("SelectedLegalStatusLookupId", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsOfficialLanguageSelect(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("SelectedOfficialLanguageLookupId", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsSpokenLanguageSelect(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("SelectedSpokenLanguageLookupId", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsPatientStatusSelect(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("SelectedPatientStatusLookupId", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsPatientStatusDateChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<Date>("value");
        demoModel = demoModel.set("PatientStatusDate", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsProfessionChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("Profession", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsEmployerChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("Employer", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsNotesChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("Notes", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsFathersFirstNameChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("FathersFirstName", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }

    private DemographicsFathersLastNameChange(request: LocalEvent, store: PatientStore): void {
        let demoModel = store.PatientProfileDemographicModels[request.PatientId];
        let value = request.Get<string>("value");
        demoModel = demoModel.set("FathersLastName", value);
        store.PatientProfileDemographicModels[request.PatientId] = demoModel;
    }
}

export { PatientProfileDemographicsHandler };
